import { BCTreeList } from '@bimser/components';
import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import IconAction from '@bimser/icons/16/action';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { DataGrid2 } from '../..';
import { IPropertyField } from '../../../../entities/common';
import { filterFields } from '../../helper';
import { generateColumns, onBeforeToolbarItemClick } from '../DataGrid2/helpers';
import Styles from './assets/styles.scss';
import { ITreeListProperties } from './entities';

const remote = { filtering: true, sorting: true };

export default class TreeList extends DataGrid2 {
	parentIdExpr: string;
	rootValue: any;
	rootValueType: string;
	autoExpandAll: boolean;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.TreeList;
		this.__excludeChangedProperties = ['exportToExcel'];
	}

	async __loadProperties(data: ITreeListProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.parentIdExpr = data.parentIdExpr;
		this.__proxyChanged.rootValue = data.rootValue;
		this.__proxyChanged.rootValueType = data.rootValueType;
		this.__proxyChanged.autoExpandAll = data.autoExpandAll;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			parentIdExpr: this.parentIdExpr,
			rootValue: this.rootValue,
			rootValueType: this.rootValueType,
			autoExpandAll: this.autoExpandAll
		};
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...filterFields(super.__getPropertyFields(), ['exportToExcel']),
			{
				name: 'parentIdExpr',
				type: 'string'
			},
			{
				name: 'rootValue',
				type: 'any'
			},
			{
				name: 'rootValueType',
				type: 'string'
			},
			{
				name: 'autoExpandAll',
				type: 'boolean'
			}
		];
	}

	__getRootValue() {
		if (isNullOrUndefined(this.rootValue)) {
			return null;
		}

		if (isNullOrUndefined(this.rootValueType)) {
			return null;
		} else if (this.rootValueType === 'integer') {
			return Number(this.rootValue);
		}

		return this.rootValue.toString();
	}

	__render() {
		const style: IDictionary<string> = {
			'--backgroundColor': this.style.backgroundColor
		};

		if (this.alternateColor.enabled) {
			style['--alternateColor'] = this.alternateColor.color;
		}

		return super.__render(
			<BCTreeList.Components.BCTreeList
				parentIdExpr={this.parentIdExpr}
				rootValue={this.__getRootValue()}
				autoExpandAll={this.autoExpandAll}
				ref={this.__bcDataGridRef as any}
				cacheEnabled={this.cacheSettingsEnabled}
				className={Styles.treeList}
				columnAutoWidth={this.columnAutoWidth}
				disabled={this.__isDisabled()}
				showColumnLines={this.gridLines === 'vertical' || this.gridLines === 'both'}
				showRowLines={this.gridLines === 'horizontal' || this.gridLines === 'both'}
				remoteOperations={remote}
				dataSource={this.__store}
				hoverStateEnabled={this.hoverStateEnabled}
				selectedRowKeys={this.selectedRowKeys}
				onSelectionChanged={this.__onSelectionChanged}
				rowAlternationEnabled={this.alternateColor.enabled}
				allowColumnResizing={this.columnSettings.resizeable}
				columnResizingMode={this.columnSettings.resizingMode}
				onEditorPreparing={this.__onEditorPreparing}
				allowColumnReordering={this.columnSettings.orderable}
				columnHidingEnabled={this.columnHidingEnabled}
				onInitNewRow={this.__onInitNewRow}
				showBorders={this.gridBorder}
				onRowDblClick={this.__onRowDblClick}
				onRowRemoving={this.__onRowRemoving}
				onRowRemoved={this.__onRowRemoved}
				onRowInserting={this.__onRowInserting}
				onRowInserted={this.__onRowInserted}
				onRowUpdating={this.__onRowUpdating}
				onRowUpdated={this.__onRowUpdated}
				onEditingStart={this.__onEditingStart}
				onCellPrepared={this.__onCellPrepared}
				onEditCanceling={this.__onEditCanceling}
				height={this.__calculateHeight()}
				width="100%"
				style={style}
				cellHintEnabled
				repaintChangesOnly
			>
				{!this.virtualScrolling && (
					<BCTreeList.Components.Paging
						enabled={this.paging.enabled ?? true}
						defaultPageIndex={this.paging.currentPage - 1}
						defaultPageSize={this.paging.pageSize}
					/>
				)}
				{!this.virtualScrolling && (
					<BCTreeList.Components.Pager
						allowedPageSizes={this.paging.pagingSizes}
						showPageSizeSelector={this.paging.enabled}
						visible
						showInfo
						showNavigationButtons
					/>
				)}
				<BCTreeList.Components.FilterRow
					visible={this.filtering.enabled}
					showOperationChooser={this.filtering.showOperationChooser}
				/>
				<BCTreeList.Components.HeaderFilter visible={this.filtering.headerFilterEnabled} />
				<BCTreeList.Components.SearchPanel visible={this.searching.enabled} />
				<BCTreeList.Components.ColumnChooser
					enabled={this.columnSettings.showColumnChooser}
				/>
				<BCTreeList.Components.ColumnFixing enabled={this.columnFixingEnabled} />
				{this.virtualScrolling && (
					<BCTreeList.Components.Scrolling
						mode="virtual"
						columnRenderingMode="virtual"
						rowRenderingMode="virtual"
						renderAsync
						showScrollbar="always"
					/>
				)}
				{this.sorting.enabled && <BCTreeList.Components.Sorting mode={this.sorting.mode} />}
				{this.editing.mode && !this.__isReadOnly() && (
					<BCTreeList.Components.Editing
						mode={this.editing.mode}
						allowAdding={this.editing.showAddCommand}
						allowUpdating={this.editing.showEditCommand}
						allowDeleting={this.editing.showDeleteCommand}
						onChangesChange={this.__onChangesChange}
					/>
				)}
				{this.selection.enabled && !this.__isReadOnly() && (
					<BCTreeList.Components.Selection
						mode={this.selection.mode}
						allowSelectAll={this.selection.showSelectAll}
					/>
				)}
				{this.rowDragging.enabled && (
					<BCTreeList.Components.RowDragging
						showDragIcons={this.rowDragging.showDragIcons}
					/>
				)}
				<BCTreeList.Components.Toolbar>
					<BCTreeList.Components.Item
						key="addRowButton"
						name="addRowButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.addRowButtonOptions}
					/>

					<BCTreeList.Components.Item
						key="columnChooserButton"
						name="columnChooserButton"
						locateInMenu="auto"
					/>

					<BCTreeList.Components.Item
						key="revertButton"
						name="revertButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.revertButtonOptions}
					/>

					<BCTreeList.Components.Item
						key="saveButton"
						name="saveButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.saveButtonOptions}
						visible={false}
					/>

					<BCTreeList.Components.Item
						key="clearAll"
						widget="dxButton"
						location="after"
						options={this.__toolbarButtonOptions.clearAllOptions}
						locateInMenu="auto"
						visible={this.showClearAllButton}
						showText="inMenu"
					/>

					<BCTreeList.Components.Item
						key="filterClear"
						widget="dxButton"
						location="after"
						options={this.__toolbarButtonOptions.filterClearOptions}
						locateInMenu="auto"
						visible={this.filtering.enabled}
						showText="inMenu"
					/>

					<BCTreeList.Components.Item
						key="forceRefresh"
						widget="dxButton"
						location="after"
						options={this.__toolbarButtonOptions.forceRefreshOptions}
						locateInMenu="auto"
						visible
						showText="inMenu"
					/>

					<BCTreeList.Components.Item
						key="searchPanel"
						name="searchPanel"
						locateInMenu="auto"
					/>

					{this.toolbarActionButtons.map((toolbarActionButton, index) => {
						const text = MLHelper.getMLText(toolbarActionButton.text);
						const hint = MLHelper.getMLText(toolbarActionButton.title);
						const icon = GetIcon16ByName(
							toolbarActionButton.icon,
							IconAction
						).info.getElement();

						if (!this.__toolbarActionButtonOptions.at(index)) {
							this.__toolbarActionButtonOptions.push({
								original: cloneDeep(toolbarActionButton),
								options: {
									hint,
									text,
									icon,
									onClick: () => {
										onBeforeToolbarItemClick(this, toolbarActionButton);
									}
								}
							});
						} else if (
							!isEqual(
								this.__toolbarActionButtonOptions.at(index).original,
								toolbarActionButton
							)
						) {
							this.__toolbarActionButtonOptions[index] = {
								original: cloneDeep(toolbarActionButton),
								options: {
									hint,
									text,
									icon,
									onClick: () => {
										onBeforeToolbarItemClick(this, toolbarActionButton);
									}
								}
							};
						}

						return (
							<BCTreeList.Components.Item
								key={toolbarActionButton.name}
								visible={toolbarActionButton.visible}
								widget="dxButton"
								locateInMenu="auto"
								showText="always"
								location="before"
								options={this.__toolbarActionButtonOptions.at(index).options}
							/>
						);
					})}
				</BCTreeList.Components.Toolbar>
				{BCTreeList.Components.editorColumnButtons()}
				{generateColumns(this, this.columns)}
			</BCTreeList.Components.BCTreeList>
		);
	}
}
